import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verifyEmail = useAuthStore((s) => s.verifyEmail);
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [error, setError] = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setError("No verification token provided");
      return;
    }

    // Prevent StrictMode double-call — verify_email deletes the token on first use
    if (calledRef.current) return;
    calledRef.current = true;

    async function verify() {
      try {
        await verifyEmail(token!);
        setStatus("success");
        const savedReturnTo = localStorage.getItem("returnTo");
        if (savedReturnTo) {
          localStorage.removeItem("returnTo");
          setTimeout(() => navigate(savedReturnTo), 1500);
        } else {
          setTimeout(() => navigate("/dashboard"), 1500);
        }
      } catch (err) {
        setStatus("error");
        setError(err instanceof Error ? err.message : "Verification failed");
      }
    }

    verify();
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "verifying") {
    return (
      <div className="space-y-5 text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        <p className="text-sm text-ink-3">Verifying your email...</p>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ok/20">
          <svg className="h-7 w-7 text-ok" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-ink">Email verified</h2>
        <p className="text-sm text-ink-3">Redirecting...</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-bad/20">
        <svg className="h-7 w-7 text-bad" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-ink">Verification failed</h2>
      <p className="text-sm text-bad">{error}</p>
      <p className="text-sm text-ink-3">
        The link may have expired.{" "}
        <Link to="/login" className="text-ink-2 transition-colors hover:text-ink">
          Sign in
        </Link>{" "}
        to request a new verification email.
      </p>
    </div>
  );
}
